import { type RefObject, useEffect, useState } from 'react';

/**
 * Isolated plugin views do not inherit the app's CSS, so the host sends its resolved theme:
 * the color scheme and the design tokens (`--background`, `--foreground`, …) currently in effect.
 * Views apply them with the lowest precedence, so a plugin's own `:root` values still win.
 */
export type PluginHostTheme = Readonly<{
  colorScheme: 'light' | 'dark';
  /** The app's `data-theme`, so a view importing the design system resolves the same tokens. */
  dataTheme: string | null;
  tokens: Readonly<Record<string, string>>;
}>;

const TOKEN_NAME = /^--[a-zA-Z0-9-]{1,80}$/;
const MAX_TOKENS = 512;
const THEME_SELECTOR = /:root|\bhtml\b|\[data-theme|\.dark\b|\.light\b/;

function collectTokenNames(rules: CSSRuleList, names: Set<string>) {
  for (const rule of Array.from(rules)) {
    if (names.size >= MAX_TOKENS) return;
    if (rule instanceof CSSStyleRule && THEME_SELECTOR.test(rule.selectorText)) {
      for (const property of Array.from(rule.style))
        if (property.startsWith('--') && TOKEN_NAME.test(property)) names.add(property);
    }
    const nested = (rule as CSSRule & { cssRules?: CSSRuleList }).cssRules;
    if (nested) collectTokenNames(nested, names);
  }
}

/** The app's theme token names, read from its own stylesheets. */
function readTokenNames(): string[] {
  const names = new Set<string>();
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      collectTokenNames(sheet.cssRules, names);
    } catch {
      // Cross-origin stylesheets cannot be read; the app's own theme is same-origin.
    }
  }
  return [...names].sort();
}

/**
 * One store for every frame: the token names (read by walking the stylesheets) and the watchers
 * of the app's theme markers, shared while any frame is mounted.
 */
let tokenNames: string[] | null = null;
const themeListeners = new Set<() => void>();
let stopWatchingTheme: (() => void) | null = null;

function hostThemeTokenNames(): string[] {
  tokenNames ??= readTokenNames();
  return tokenNames;
}

function watchHostTheme(listener: () => void): () => void {
  themeListeners.add(listener);
  if (!stopWatchingTheme) {
    const notify = () => {
      for (const current of themeListeners) current();
    };
    const root = new MutationObserver(notify);
    root.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'style'],
    });
    // Stylesheets can load after the first snapshot (development and lazy chunks).
    const head = new MutationObserver(() => {
      tokenNames = null;
      notify();
    });
    head.observe(document.head, { childList: true });
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    media?.addEventListener?.('change', notify);
    stopWatchingTheme = () => {
      root.disconnect();
      head.disconnect();
      media?.removeEventListener?.('change', notify);
      tokenNames = null;
    };
  }
  return () => {
    themeListeners.delete(listener);
    if (themeListeners.size > 0) return;
    stopWatchingTheme?.();
    stopWatchingTheme = null;
  };
}

/**
 * The theme in effect where the view is mounted: tokens can be scoped below the root (for
 * example `.desktop-shell[data-theme]`), so they are resolved on the view's host element.
 */
function hostThemeSnapshot(element: Element = document.documentElement): PluginHostTheme {
  const names = hostThemeTokenNames();
  const style = getComputedStyle(element);
  const tokens: Record<string, string> = {};
  for (const name of names) {
    const value = style.getPropertyValue(name).trim();
    if (value) tokens[name] = value;
  }
  const root = document.documentElement;
  const dataTheme =
    (element.closest('[data-theme]') as HTMLElement | null)?.dataset['theme'] ??
    root.dataset['theme'] ??
    null;
  const scheme = style.getPropertyValue('color-scheme').trim();
  // An explicit class or theme attribute wins; `light dark` only says both are supported.
  const dark =
    root.classList.contains('dark') || (dataTheme ?? '').endsWith('dark')
      ? true
      : root.classList.contains('light') || (dataTheme ?? '').endsWith('light')
        ? false
        : scheme === 'dark' ||
          (scheme !== 'light' &&
            window.matchMedia?.('(prefers-color-scheme: dark)').matches === true);
  return {
    colorScheme: dark ? 'dark' : 'light',
    dataTheme: dataTheme && /^[a-z0-9-]{1,40}$/.test(dataTheme) ? dataTheme : null,
    tokens,
  };
}

function sameTheme(left: PluginHostTheme, right: PluginHostTheme) {
  if (left.colorScheme !== right.colorScheme || left.dataTheme !== right.dataTheme) return false;
  const keys = Object.keys(left.tokens);
  return (
    keys.length === Object.keys(right.tokens).length &&
    keys.every((key) => left.tokens[key] === right.tokens[key])
  );
}

/** The app's current theme, updated when the theme class, attribute or OS preference changes. */
export function useHostTheme(host?: RefObject<Element | null>): PluginHostTheme {
  const [theme, setTheme] = useState(() => hostThemeSnapshot());
  useEffect(() => {
    const element = () => host?.current ?? document.documentElement;
    const refresh = () => {
      const next = hostThemeSnapshot(element());
      setTheme((current) => (sameTheme(current, next) ? current : next));
    };
    const stop = watchHostTheme(refresh);
    // A theme scoped below the root (for example `.desktop-shell[data-theme]`) is this view's own.
    const themed = element().closest('[data-theme]');
    const scoped =
      themed && themed !== document.documentElement ? new MutationObserver(refresh) : null;
    if (themed)
      scoped?.observe(themed, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'style'],
      });
    refresh();
    return () => {
      stop();
      scoped?.disconnect();
    };
  }, [host]);
  return theme;
}
